/**
 * Story Beats tab (AI Playground → Memory Archive).
 *
 * Admin/super-admin observability surface for the Coverage Phase C Story Beats:
 * captured Scene + Reflection per beat, the multi-card cross-tags from
 * `story_beat_cards` (the headline — "also tagged → Education (0.80)"), and
 * the missing attributes the paranoid extractor dropped. Grouped by primary
 * card, filterable by card, paginated. Reads the admin User-Insights API under
 * the current "Acting as <user>" identifier.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Link2, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  beatCoverageQueryKey,
  fetchBeatCoverage,
  fetchStoryBeats,
  storyBeatsQueryKey,
  type AttrValue,
  type BeatReflection,
  type BeatScene,
  type StoryBeat,
} from '@/services/story-beats';

const PAGE_SIZE = 20;

interface StoryBeatsTabProps {
  /** Target user's UUID (or admin's own id when not impersonating). */
  identifier: string | null;
  /** Currently selected primary-card filter (story_cards UUID), or null. */
  cardFilter: string | null;
  onCardFilterChange: (cardUuid: string | null) => void;
}

export function StoryBeatsTab({ identifier, cardFilter, onCardFilterChange }: StoryBeatsTabProps) {
  const [offset, setOffset] = useState(0);

  // Paging is local; reset it whenever the user or the card filter changes so
  // we never land on an out-of-range page of a different result set.
  useEffect(() => {
    setOffset(0);
  }, [identifier, cardFilter]);

  const coverageQuery = useQuery({
    queryKey: beatCoverageQueryKey(identifier),
    queryFn: () => fetchBeatCoverage(identifier!),
    enabled: !!identifier,
    staleTime: 30_000,
  });

  const beatsQuery = useQuery({
    queryKey: storyBeatsQueryKey(identifier, { cardId: cardFilter, limit: PAGE_SIZE, offset }),
    queryFn: () => fetchStoryBeats(identifier!, { cardId: cardFilter, limit: PAGE_SIZE, offset }),
    enabled: !!identifier,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const beats = beatsQuery.data?.items ?? [];
  const total = beatsQuery.data?.total ?? 0;
  const coverageCards = coverageQuery.data?.cards ?? [];
  const groups = useMemo(() => groupByPrimaryCard(beats), [beats]);

  if (!identifier) {
    return (
      <EmptyState
        title="Select a user"
        body="Story beats are an admin observability surface. Pick a test user above to view the beats captured for them."
      />
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
      <aside className="space-y-4">
        <div className="rounded-lg border bg-card p-4">
          <h3 className="font-display text-sm font-semibold">Story card</h3>
          <ul className="mt-2 space-y-1 text-sm">
            <li>
              <button
                type="button"
                onClick={() => onCardFilterChange(null)}
                className={cn(
                  'w-full rounded px-2 py-1 text-left',
                  cardFilter === null
                    ? 'bg-orb-gold/15 font-medium text-orb-teal'
                    : 'text-muted-foreground hover:bg-accent'
                )}
              >
                All cards
              </button>
            </li>
            {coverageCards.map((c) => (
              <li key={c.cardUuid}>
                <button
                  type="button"
                  onClick={() => onCardFilterChange(c.cardUuid)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left',
                    cardFilter === c.cardUuid
                      ? 'bg-orb-gold/15 font-medium text-orb-teal'
                      : 'text-muted-foreground hover:bg-accent'
                  )}
                >
                  <span className="truncate">{c.title}</span>
                  <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                    {c.beats}/{coverageQuery.data?.target ?? 3}
                  </span>
                </button>
              </li>
            ))}
            {coverageQuery.isLoading && (
              <li className="px-2 py-1 text-xs text-muted-foreground">Loading cards…</li>
            )}
          </ul>
        </div>
      </aside>

      <section aria-label="Story beats">
        {beatsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading story beats…
          </div>
        ) : beatsQuery.isError ? (
          <EmptyState
            title="Couldn’t load story beats"
            body="The beats endpoint returned an error. Confirm you have admin access for this user."
          />
        ) : beats.length === 0 ? (
          <EmptyState
            title="No story beats captured yet"
            body="Beats appear here after a session is processed by the Curator. Try clearing the card filter, or run an interview for this user."
          />
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.key}>
                <h3 className="mb-2 flex items-baseline gap-2 font-display text-base font-semibold text-foreground">
                  {group.title}
                  <span className="text-xs font-normal text-muted-foreground">
                    {group.beats.length} {group.beats.length === 1 ? 'beat' : 'beats'}
                  </span>
                </h3>
                <ul className="space-y-3">
                  {group.beats.map((beat) => (
                    <li key={beat.id}>
                      <BeatCard beat={beat} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <Pagination
              offset={offset}
              pageSize={PAGE_SIZE}
              total={total}
              fetching={beatsQuery.isFetching}
              onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              onNext={() => setOffset((o) => o + PAGE_SIZE)}
            />
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Beat card ───────────────────────────────────────────────────────────────

function BeatCard({ beat }: { beat: StoryBeat }) {
  const crossTags = beat.mappedCards.filter((m) => !m.isPrimary);
  const created = beat.createdAt ? new Date(beat.createdAt) : null;

  return (
    <article className="rounded-lg border bg-card p-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-semibold text-foreground">
            {beat.card?.title ?? 'Unknown card'}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {created ? created.toLocaleDateString() : '—'} · session{' '}
            <span className="font-mono">{beat.sessionId.slice(0, 8)}</span> · {beat.source}
          </p>
        </div>
        <Badge variant="outline" className="shrink-0 tabular-nums" title="Beat quality score">
          quality {Math.round(beat.beatQualityScore * 100)}%
        </Badge>
      </header>

      {/* Cross-tags — the headline: which OTHER cards this beat is tagged to. */}
      {crossTags.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5 text-orb-teal" aria-hidden="true" />
          <span className="text-[11px] font-medium text-muted-foreground">also tagged →</span>
          {crossTags.map((tag) => (
            <Badge
              key={tag.cardUuid}
              className="border-orb-gold/50 bg-orb-gold/10 font-normal text-orb-teal tabular-nums"
              title={`Cross-tagged to ${tag.title} with relevance ${tag.relevanceScore.toFixed(2)}`}
            >
              {tag.title} ({tag.relevanceScore.toFixed(2)})
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <SceneBlock scene={beat.scene} score={beat.sceneScore} />
        <ReflectionBlock reflection={beat.reflection} score={beat.reflectionScore} />
      </div>

      {beat.followupSuggested && (
        <p className="mt-3 rounded bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Follow-up: </span>
          {beat.followupSuggested}
        </p>
      )}
    </article>
  );
}

// ─── Scene / Reflection blocks ───────────────────────────────────────────────

function SceneBlock({ scene, score }: { scene: BeatScene | null; score: number }) {
  return (
    <BeatSection title="Scene" subtitle="who · when · where · what" score={score}>
      {scene ? (
        <>
          <AttrLine label="Who" values={scene.characters} />
          <AttrLine label="When" values={scene.time ? [scene.time] : []} />
          <AttrLine label="Where" values={scene.place ? [scene.place] : []} />
          <AttrLine label="What" values={scene.action ? [scene.action] : []} />
          <MissingAttrs missing={scene.missing_attributes} />
        </>
      ) : (
        <p className="text-xs text-muted-foreground">No scene captured.</p>
      )}
    </BeatSection>
  );
}

function ReflectionBlock({
  reflection,
  score,
}: {
  reflection: BeatReflection | null;
  score: number;
}) {
  return (
    <BeatSection title="Reflection" subtitle="why · felt then · feels now" score={score}>
      {reflection ? (
        <>
          <AttrLine label="What" values={reflection.action ? [reflection.action] : []} />
          <AttrLine label="Why" values={reflection.catalyst ? [reflection.catalyst] : []} />
          <AttrLine label="Felt then" values={reflection.emotion_then} />
          <AttrLine label="Feels now" values={reflection.emotion_now} />
          <MissingAttrs missing={reflection.missing_attributes} />
        </>
      ) : (
        <p className="text-xs text-muted-foreground">No reflection captured.</p>
      )}
    </BeatSection>
  );
}

function BeatSection({
  title,
  subtitle,
  score,
  children,
}: {
  title: string;
  subtitle: string;
  score: number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-background/40 p-3">
      <div className="flex items-baseline justify-between">
        <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-foreground">
          {title}
        </h4>
        <span className="text-[10px] tabular-nums text-muted-foreground">
          {Math.round(score * 100)}%
        </span>
      </div>
      <p className="text-[10px] text-muted-foreground">{subtitle}</p>
      <div className="mt-2 space-y-1.5">{children}</div>
    </div>
  );
}

/**
 * One attribute slot. Each captured value is shown with its provenance quote
 * (verbatim user words) inline in muted italics — that quote is the whole
 * point of the paranoid extractor, so we surface it rather than hide it.
 */
function AttrLine({ label, values }: { label: string; values: AttrValue[] }) {
  return (
    <div className="text-xs">
      <span className="font-medium text-muted-foreground">{label}: </span>
      {values.length === 0 ? (
        <span className="text-muted-foreground/60">—</span>
      ) : (
        <span className="text-foreground">
          {values.map((v, i) => (
            <span key={`${v.value}-${i}`}>
              {i > 0 && ', '}
              <span title={v.quote}>{v.value}</span>
              <span className="ml-1 italic text-muted-foreground">“{v.quote}”</span>
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

function MissingAttrs({ missing }: { missing: string[] }) {
  if (!missing || missing.length === 0) return null;
  return (
    <div className="flex items-center gap-1 pt-0.5 text-[11px] text-amber-600 dark:text-amber-500">
      <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>Missing: {missing.join(', ')}</span>
    </div>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────────────

function Pagination({
  offset,
  pageSize,
  total,
  fetching,
  onPrev,
  onNext,
}: {
  offset: number;
  pageSize: number;
  total: number;
  fetching: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + pageSize, total);
  const hasPrev = offset > 0;
  const hasNext = offset + pageSize < total;
  if (!hasPrev && !hasNext) return null;

  return (
    <div className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
      <span className="tabular-nums">
        Showing {from}–{to} of {total}
      </span>
      <div className="flex items-center gap-2">
        {fetching && <Loader2 className="h-3 w-3 animate-spin" />}
        <Button type="button" variant="outline" size="sm" disabled={!hasPrev} onClick={onPrev}>
          <ChevronLeft className="mr-1 h-3 w-3" /> Prev
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={!hasNext} onClick={onNext}>
          Next <ChevronRight className="ml-1 h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface BeatGroup {
  key: string;
  title: string;
  beats: StoryBeat[];
}

function groupByPrimaryCard(beats: StoryBeat[]): BeatGroup[] {
  const order: string[] = [];
  const map = new Map<string, BeatGroup>();
  for (const beat of beats) {
    const key = beat.card?.id ?? beat.primaryCardId;
    let group = map.get(key);
    if (!group) {
      group = { key, title: beat.card?.title ?? 'Unknown card', beats: [] };
      map.set(key, group);
      order.push(key);
    }
    group.beats.push(beat);
  }
  return order.map((k) => map.get(k)!);
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
